import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

/**
 * La escala `field` (44 px) no es un capricho: es el tamaño mínimo que permite
 * acertar con el dedo, con guantes y en movimiento. Se usa en las pantallas de
 * obra. En oficina, con ratón, `sm` y `md` aprovechan mejor el espacio.
 */
const button = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium ' +
    'transition-colors select-none whitespace-nowrap ' +
    'disabled:pointer-events-none disabled:opacity-50 ' +
    '[&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-brand text-fg-on-brand hover:bg-brand-hover shadow-card',
        secondary: 'bg-surface text-fg border border-border-strong hover:bg-surface-2 shadow-card',
        ghost: 'text-fg-muted hover:bg-surface-2 hover:text-fg',
        danger: 'bg-danger text-white hover:opacity-90 shadow-card',
        soft: 'bg-brand-soft text-brand-soft-fg hover:brightness-95',
        link: 'text-brand underline-offset-4 hover:underline',
      },
      size: {
        xs: 'h-7 px-2 text-xs [&_svg]:size-3.5',
        sm: 'h-8 px-3 text-sm [&_svg]:size-4',
        md: 'h-9 px-4 text-sm [&_svg]:size-4',
        lg: 'h-11 px-5 text-base [&_svg]:size-5',
        field: 'h-11 min-w-11 px-5 text-base [&_svg]:size-5',
        icon: 'size-9 [&_svg]:size-4',
        'icon-sm': 'size-8 [&_svg]:size-4',
        'icon-field': 'size-11 [&_svg]:size-5',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof button> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, loading, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(button({ variant, size }), className)}
        disabled={disabled ?? loading}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="animate-spin" aria-hidden />
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';
